import React, { forwardRef, useEffect, useState, useRef } from 'react';
import './Sidebar.css';
import sidebarImage from '../../assets/CommunityHospitalLogo.jpg';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSidebar } from '../../slicers/sidebarSlice';
import { InboxIcon, TemplateManagementIcon, HardwareManagementIcon, OrganizationSetupIcon, PatientManagementIcon, RevenueManagementIcon, AdmitPatientIcon, HamburgerMenu, WrenchIcon } from '../../components/icons/Icons';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import AppTourPopup from '../app-tour/AppTourPopup';
import { setSelectedMenuItem } from '../../slicers/sidebarSlice';
import { formatDateWithFullMonth } from '../../utils/dateUtils';
import { closeAlert } from '../../components/PerinHealthAlertSlice';
import { openConfirmation } from '../../components/ModalDirtyConfirmationSlice';
import {
  setOnboardingPendingPath,
} from '../../slicers/OnboardingNavigationGuardSlice';
import { hasPermission } from '../../utils/permissionUtils';
import Logger from '../../services/Logger';
import { LogElements, LogAction } from '../../services/LogElements';

const Sidebar = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sidebarSlicer = useSelector((state) => state.sidebar);
  const appTour = useSelector((state) => state.appTour);
  const onboardingInProgress = useSelector((state) => state.onboardingNavigationGuard.inProgress);
  const isSidebarOpen = sidebarSlicer.isSidebarOpen;
  const selectedMenuItem = sidebarSlicer.selectedMenuItem;
  const userInfo = useSelector((state) => state.userInfo);
  const dateFormat = useSelector((state) => state.userInfo.dateFormat);

  const { isAdmin, permissions, hasInpatientGroup } = useSelector((state) => state.userInfo);

  const isPermissionExists = (permissionName) => hasPermission(permissions, permissionName);

  const sidebarWidth = isSidebarOpen ? '239px' : '60px';

  const sidebarRef = useRef(null); // Ref for sidebar

  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  });

  const formatSidebarDate = (date) => {
    if (!date) return '';
    return formatDateWithFullMonth(date.toISOString(), dateFormat);
  };

  const formattedDate = formatSidebarDate(currentDateTime);

  const formattedTime = currentDateTime.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  const admitPatient = () => {
    // Log the enroll patient button click
    Logger.shared.info(
      LogElements.enrollPatientButton, 
      LogAction.clicked,
      'Sidebar.js',
      'Start'
    );
    
    if (onboardingInProgress) {
      dispatch(setOnboardingPendingPath('/enroll-patient'));
      dispatch(
        openConfirmation({
          header: 'Confirmation',
          message: 'Onboarding is still in progress. Leave anyway?',
          buttons: [
            { label: 'No', variant: 'secondary', onClickKey: 'NO' },
            { label: 'Yes', variant: 'primary', onClickKey: 'YES_ONBOARDING' },
          ],
        })
      );
      return;
    }
    navigate('/enroll-patient');
    dispatch(setSelectedMenuItem('Patient Enrollment'));
  };

  const openOnboardingLeaveConfirmation = (targetPath) => {
    dispatch(setOnboardingPendingPath(targetPath));
    dispatch(
      openConfirmation({
        header: 'Confirmation',
        message: 'Onboarding is still in progress. Leave anyway?',
        buttons: [
          { label: 'No', variant: 'secondary', onClickKey: 'NO' },
          { label: 'Yes', variant: 'primary', onClickKey: 'YES_ONBOARDING' },
        ],
      })
    );
  };

  const handleSidebarMenuClickCapture = (event) => {
    if (!onboardingInProgress) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('/')) return;
    if (href === '/onboarding') return;

    event.preventDefault();
    event.stopPropagation();
    openOnboardingLeaveConfirmation(href);
  };

  // Create refs for the sidebar menu items
  const dashboardRef = useRef(null);
  const patientEnrollmentRef = useRef(null);
  const appointmentsRef = useRef(null);
  const tasksRef = useRef(null);
  const billingRef = useRef(null);
  const newOrgRef = useRef(null);
  const orgManagementRef = useRef(null);
  const onboardingRef = useRef(null);
  const accountManagementRef = useRef(null);
  const roleManagementRef = useRef(null);
  const hubManagementRef = useRef(null);
  const deviceManagementRef = useRef(null);
  const contentManagementRef = useRef(null);
  const questionnaireManagementRef = useRef(null);
  const programManagementRef = useRef(null);
  const patientMessageRef = useRef(null);
  const referenceCodeRef = useRef(null);
  const careTeamMessageRef = useRef(null);
  const patientSubmittedReportRef = useRef(null);
  const deviceRegistryRef = useRef(null);
  const serviceHistoryRef = useRef(null);
  const securityManagementRef = useRef(null);

  useEffect(() => {
    if (appTour.showAppTour) {
      const currentRef = getCurrentRef(appTour.currentStepLabel);
      if (currentRef && currentRef.current) {
        currentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [appTour.currentStepIndex, appTour.showAppTour]);

  const getCurrentRef = (stepLabel) => {
    switch (stepLabel) {
      case 'Patient Dashboard': return dashboardRef;
      case 'Patient Enrollment': return patientEnrollmentRef;
      case 'Appointments': return appointmentsRef;
      case 'Billing': return billingRef;
      case 'Tasks': return tasksRef;
      case 'Add New Organization': return newOrgRef;
      case 'Organization Hierarchy Management': return orgManagementRef;
      case 'Account Management': return accountManagementRef;
      case 'Role Management': return roleManagementRef;
      case 'Hub Management': return hubManagementRef;
      case 'Device Management': return deviceManagementRef;
      case 'Content Management': return contentManagementRef;
      case 'Questionnaire Management': return questionnaireManagementRef;
      case 'Program Management': return programManagementRef;
      case 'Patient Messages': return patientMessageRef;
      case 'Staff Messages': return careTeamMessageRef;
      case 'Patient Medical Reports': return patientSubmittedReportRef;
      case 'Reference Code Management': return referenceCodeRef;
      case 'Device Registry': return deviceRegistryRef;
      case 'Onboarding': return onboardingRef;
      default: return null;
    }
  };

  const handleNavigation = (menuItem) => {
    // Log sidebar navigation
    Logger.shared.info(
      LogElements[`sidebar${menuItem.replace(/\s+/g, '')}`] || LogElements.sidebarDashboard,
      LogAction.clicked,
      'Sidebar.js',
      'Start'
    );
    
    dispatch(setSelectedMenuItem(menuItem));
    // close any open alerts
    dispatch(closeAlert());
  };

  return (
    <div className={`sidebar`} style={{ width: sidebarWidth }} ref={sidebarRef}>
      <div style={{display:"flex", flexDirection:"column", paddingBottom:"10px"}}>
        <div style={{ display: "grid", justifyContent: "center", paddingTop: "26px"}}>
          <div className="hamburger" style={{ paddingLeft: isSidebarOpen ? '10px' : '15px'}}  onClick={() => { dispatch(toggleSidebar()) }}>
            <HamburgerMenu />
          </div>
          {isSidebarOpen && (
            <div style={{ alignSelf:"center"}}>
              <img src={userInfo.organizationLogo || sidebarImage} alt="Sidebar Logo" className="sidebar-logo"/>
            </div>
          )}
        </div>
        {isSidebarOpen && isPermissionExists('Patient Enrollment') &&
          <div className="admit-button-container">
            <button className="admit-button" onClick={admitPatient}>
              <AdmitPatientIcon />
              <div>Enroll Patient</div>
            </button>
          </div>
        }
      </div>
      {isSidebarOpen && (
        <ul
          className={`sidebar-menu ${appTour.showAppTour ? `sidebar-open-scroll-onboarding` : `sidebar-open-scroll`}`}
          onClickCapture={handleSidebarMenuClickCapture}
        >
          <li className="category-header">
            <PatientManagementIcon />
            <span className="category-header-title">Patient Management</span>
          </li>
          {isPermissionExists('Patient Dashboard') &&
            <Link to="/overview-dashboard">
              <li ref={dashboardRef} className={selectedMenuItem === 'Dashboard' ? 'selected' : ''} onClick={() => handleNavigation('Dashboard')}>
                <span>Dashboard</span>
              </li>
            </Link>
          }
          {isPermissionExists('Patient Enrollment') &&
            <Link to="/patient-enrollment">
              <li ref={patientEnrollmentRef} className={selectedMenuItem === 'Enrollment' ? 'selected' : ''} onClick={() => handleNavigation('Enrollment')}>
                <span>Enrollment</span>
              </li>
            </Link>
          }
          {isPermissionExists('Appointments') &&
            <Link to="/patient-appointment">
              <li ref={appointmentsRef} className={selectedMenuItem === 'Appointments' ? 'selected' : ''} onClick={() => handleNavigation('Appointments')}>
                <span>Appointments</span>
                {sidebarSlicer?.appointmentsCount > 0 && (<Badge bg="success" pill='true'>{sidebarSlicer.appointmentsCount}</Badge>)}
              </li>
            </Link>
          }
          {isPermissionExists('Tasks') &&
            <Link to="/tasks">
              <li ref={tasksRef} className={selectedMenuItem === 'Tasks' ? 'selected' : ''} onClick={() => handleNavigation('Tasks')}>
                <span>Tasks</span>
                {sidebarSlicer?.taskListCount > 0 && (<Badge bg="success" pill='true'>{sidebarSlicer.taskListCount}</Badge>)}
              </li>
            </Link>
          }
          <li className="blank-header"></li>
          {hasInpatientGroup && <>
          <li className="category-header">
            <PatientManagementIcon />
            <span className="category-header-title">Inpatient Management</span>
          </li>
          <Link to="/patient-location">
              <li className={selectedMenuItem === 'Patient Location' ? 'selected' : ''} onClick={() => handleNavigation('Patient Location')}>
                <span>Patient Location</span>
              </li>
            </Link>
            <Link to="/inpatient-portal">
              <li className={selectedMenuItem === 'Inpatient Portal' ? 'selected' : ''} onClick={() => handleNavigation('Inpatient Portal')}>
                <span>Inpatient Portal</span>
              </li>
            </Link>
            <li className="blank-header"></li>
            </>}
          {isPermissionExists('Billing') &&
            <>
              <li className="category-header">
                <RevenueManagementIcon />
                <span className="category-header-title">Revenue Management</span>
              </li>
              <li ref={billingRef} className={selectedMenuItem === 'Billing' ? 'selected' : ''} onClick={() => handleNavigation('Billing')}>
                <span>Billing</span>
              </li>
              <li className="blank-header"></li>
            </>
          }
          <li className="category-header">
            <OrganizationSetupIcon />
            <span className="category-header-title">Organization Setup</span>
          </li>
          {isAdmin &&
            <Link to="/new-organization">
              <li ref={newOrgRef} className={selectedMenuItem === 'Organization' ? 'selected' : ''} onClick={() => handleNavigation('Organization')}>
                <span>Organization</span>
              </li>
            </Link>
          }
          {isPermissionExists('Organization Hierarchy Management') &&
            <Link to="/organization-management">
              <li ref={orgManagementRef} className={selectedMenuItem === 'Hierarchy' ? 'selected' : ''} onClick={() => handleNavigation('Hierarchy')}>
                <span>Hierarchy</span>
              </li>
            </Link>
          }
          {isAdmin &&
            <Link to="/onboarding">
              <li ref={onboardingRef} className={selectedMenuItem === 'Onboarding' ? 'selected' : ''} onClick={() => handleNavigation('Onboarding')}>
                <span>Onboarding</span>
              </li>
            </Link>
          }
          {hasInpatientGroup &&
            <Link to="/ehr-accounts">
              <li ref={accountManagementRef} className={selectedMenuItem === 'EHR Accounts' ? 'selected' : ''} onClick={() => handleNavigation('EHR Accounts')}>
                <span>EHR Accounts</span>
              </li>
            </Link>
          }
          {/* <Link to="/security-management">
              <li ref={securityManagementRef} className={selectedMenuItem === 'Security Management' ? 'selected' : ''} onClick={() => handleNavigation('Security Management')}>
                <span>Security Management</span>
              </li>
            </Link> */}
          {isPermissionExists('Account Management') &&
            <Link to="/account-management">
              <li ref={accountManagementRef} className={selectedMenuItem === 'Accounts' ? 'selected' : ''} onClick={() => handleNavigation('Accounts')}>
                <span>Accounts</span>
              </li>
            </Link>
          }
          {isPermissionExists('Role Management') &&
            <Link to="/role-management">
              <li ref={roleManagementRef} className={selectedMenuItem === 'Roles' ? 'selected' : ''} onClick={() => handleNavigation('Roles')}>
                <span>Roles</span>
              </li>
            </Link>
          }
          {isPermissionExists('Reference Code Management') &&
            <Link to="/reference-code">
              <li ref={referenceCodeRef} className={selectedMenuItem === 'Reference Code' ? 'selected' : ''} onClick={() => handleNavigation('Reference Code')}>
                <span>Reference Code</span>
              </li>
            </Link>
          }
          {(isPermissionExists('Device Management') || isPermissionExists('Hub Management') || isPermissionExists('Device Registry')) &&
            <>
              <li className="blank-header"></li>
              <li className="category-header">
                <HardwareManagementIcon />
                <span className="category-header-title">Hardware Management</span>
              </li>
            </>
          }
          {isPermissionExists('Hub Management') &&
            <Link to="/hub-management">
              <li ref={hubManagementRef} className={selectedMenuItem === 'Hub' ? 'selected' : ''} onClick={() => handleNavigation('Hub')}>
                <span>Hub</span>
              </li>
            </Link>
          }
          {isPermissionExists('Device Management') &&
            <>
              <Link to="/device-management">
                <li ref={deviceManagementRef} className={selectedMenuItem === 'Devices' ? 'selected' : ''} onClick={() => handleNavigation('Devices')}>
                  <span>Devices</span>
                </li>
              </Link>
            </>
          }
          {isPermissionExists('Device Registry') &&
            <Link to="/device-registry">
              <li ref={deviceRegistryRef} className={selectedMenuItem === 'Device Registry' ? 'selected' : ''} onClick={() => handleNavigation('Device Registry')}>
                <span>Device Registry</span>
              </li>
            </Link>
          }
          {isPermissionExists('Tablet Management') && hasInpatientGroup &&
            <>
              <Link to="/tablet-management">
                <li className={selectedMenuItem === 'Tablets' ? 'selected' : ''} onClick={() => handleNavigation('Tablets')}>
                  <span>Tablets</span>
                </li>
              </Link>
            </>
          }
          {(isPermissionExists('Content Management') || isPermissionExists('Questionnaire') || isPermissionExists('Program')) &&
            <>
              <li className="blank-header"></li>
              <li className="category-header">
                <TemplateManagementIcon />
                <span className="category-header-title">Template Management</span>
              </li>
            </>
          }
          {isPermissionExists('Content Management') &&
            <Link to="/content-management">
              <li ref={contentManagementRef} className={selectedMenuItem === 'Content' ? 'selected' : ''} onClick={() => handleNavigation('Content')}>
                <span>Content</span>
              </li>
            </Link>
          }
          {isPermissionExists('Questionnaire Management') &&
            <Link to="/questionnaire-management">
              <li ref={questionnaireManagementRef} className={selectedMenuItem === 'Questionnaire' ? 'selected' : ''} onClick={() => handleNavigation('Questionnaire')}>
                <span>Questionnaire</span>
              </li>
            </Link>
          }
          {isPermissionExists('Program Management') &&
            <Link to="/program-management">
              <li ref={programManagementRef} className={selectedMenuItem === 'Care Program' ? 'selected' : ''} onClick={() => handleNavigation('Care Program')}>
                <span>Care Program</span>
              </li>
            </Link>
          }
          <li className="blank-header"></li>
          <li className="category-header">
            <InboxIcon />
            <span className="category-header-title">Inbox</span>
          </li>
          {isPermissionExists('Patient Messages') &&
            <Link to="/patient-message">
              <li ref={patientMessageRef} className={selectedMenuItem === 'Patient Messages' ? 'selected' : ''} onClick={() => handleNavigation('Patient Messages')}>
                <span>Patient Messages</span>
                {sidebarSlicer?.unreadPatientMessageIds?.length > 0 && (<Badge bg="success" pill='true'>{sidebarSlicer.unreadPatientMessageIds?.length}</Badge>)}
              </li>
            </Link>
          }
          {isPermissionExists('Care Team Messages') &&
            <Link to="/care-team-message">
              <li ref={careTeamMessageRef} className={selectedMenuItem === 'Staff Messages' ? 'selected' : ''} onClick={() => handleNavigation('Staff Messages')}>
                <span>Staff Messages</span>
                {sidebarSlicer?.unreadStaffMessageIds?.length > 0 && (<Badge bg="success" pill='true'>{sidebarSlicer.unreadStaffMessageIds?.length}</Badge>)}
              </li>
            </Link>
          }
          {isPermissionExists('Patient Medical Reports') &&
            <Link to="/patient-submitted-reports">
              <li ref={patientSubmittedReportRef} className={selectedMenuItem === 'Patient-Submitted Reports' ? 'selected' : ''} onClick={() => handleNavigation('Patient-Submitted Reports')}>
                <span>Patient-Submitted Reports</span>
                {sidebarSlicer?.newReportCount > 0 && (<Badge bg="success" pill='true'>{sidebarSlicer.newReportCount}</Badge>)}
              </li>
            </Link>
          }
          <li className="blank-header"></li>
          <li className="category-header">
            <WrenchIcon color="#4F4F4F" />
            <span className="category-header-title">Support</span>
          </li>
          <Link to="/service-history">
            <li ref={serviceHistoryRef} className={selectedMenuItem === 'Service History' ? 'selected' : ''} onClick={() => handleNavigation('Service History')}>
              <span>Service History</span>
            </li>
          </Link>
          <li className="blank-header"></li>
          <div className={`date-time ${isSidebarOpen ? '' : 'sidebar-minimized'}`} style={!isSidebarOpen ? { paddingLeft: '0px' } : {}}>
            <div className='date-style'>{formattedDate}</div>
            <div className='time-style'>{formattedTime}</div>
          </div>
        </ul>
      )}
      {appTour.showAppTour && (
        <AppTourPopup
          showAppTour={appTour.showAppTour}
          targetRef={getCurrentRef(appTour.currentStepLabel)}
          dashboardRef={dashboardRef}
          patientEnrollmentRef={patientEnrollmentRef}
          appointmentsRef={appointmentsRef}
          billingRef={billingRef}
          tasksRef={tasksRef}
          newOrgRef={newOrgRef}
          orgManagementRef={orgManagementRef}
          accountManagementRef={accountManagementRef}
          roleManagementRef={roleManagementRef}
          hubManagementRef={hubManagementRef}
          deviceManagementRef={deviceManagementRef}
          contentManagementRef={contentManagementRef}
          questionnaireManagementRef={questionnaireManagementRef}
          referenceCodeRef={referenceCodeRef}
          programManagementRef={programManagementRef}
          patientMessageRef={patientMessageRef}
          careTeamMessageRef={careTeamMessageRef}
          patientSubmittedReportRef={patientSubmittedReportRef}
        />
      )}
    </div>
  );
});

export default Sidebar;
